import React, {useEffect, useMemo, useState} from "react";
import {Button, Form} from "react-bootstrap";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import LoadingDataDiv from "../common/LoadingDataDiv";
import {toastError, toastSuccess} from "../../../helpers/Toast";
import "../../../css/Scheduler.css";

const typeLabels = {
  SCHEDULE_PUBLISHED: "Publikacja grafiku",
  SCHEDULE_UPDATED: "Zmiana danych grafiku",
  SCHEDULE_ARCHIVED: "Wycofanie grafiku",
  SHIFT_ADDED: "Dodana zmiana",
  SHIFT_UPDATED: "Zmiana grafiku",
  SHIFT_REMOVED: "Usunięta zmiana",
  REQUEST_APPROVED: "Wniosek zaakceptowany",
  REQUEST_REJECTED: "Wniosek odrzucony",
  SWAP_PROPOSED: "Propozycja zamiany",
  SWAP_ACCEPTED: "Zamiana zaakceptowana przez pracownika",
  SWAP_REJECTED: "Zamiana odrzucona",
  SWAP_CANCELLED: "Zamiana anulowana",
  HR_MEETING_INVITATION: "Zaproszenie na spotkanie HR",
  HR_MEETING_UPDATED: "Zmiana spotkania HR",
  HR_MEETING_CANCELLED: "Odwołanie spotkania HR",
  MANUAL: "Wiadomość od kierownika"
};

const deliveryLabels = {
  NOT_REQUESTED: "nie wybrano",
  QUEUED: "w kolejce",
  SENT: "wysłano",
  DELIVERED: "dostarczono",
  UNDELIVERED: "niedostarczono",
  FAILED: "błąd",
  NOT_CONFIGURED: "brak konfiguracji",
  NO_ADDRESS: "brak adresu"
};

const dateTime = value => value ? new Intl.DateTimeFormat("pl-PL", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit"
}).format(new Date(value)) : "";

const ScheduleNotifications = () => {
  const axiosPrivate = useAxiosPrivate();
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    try {
      const response = await axiosPrivate.get("/schedule-notifications/mine");
      setNotifications(response.data ?? []);
    } catch (error) {
      toastError(error?.response?.data?.message || "Nie udało się pobrać powiadomień.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [axiosPrivate]);

  const visible = useMemo(() => {
    if (filter === "UNREAD") return notifications.filter(item => !item.readAt);
    if (filter === "UNACKNOWLEDGED") return notifications.filter(item => !item.acknowledgedAt);
    return notifications;
  }, [notifications, filter]);

  const updateNotification = item => {
    setNotifications(current => current.map(existing => existing.id === item.id ? item : existing));
    window.dispatchEvent(new CustomEvent("schedule-notifications-changed"));
  };

  const markRead = async id => {
    setSavingId(id);
    try {
      const response = await axiosPrivate.post(`/schedule-notifications/${id}/read`);
      updateNotification(response.data);
    } catch (error) {
      toastError(error?.response?.data?.message || "Nie udało się oznaczyć powiadomienia jako przeczytane.");
    } finally {
      setSavingId(null);
    }
  };

  const acknowledge = async id => {
    setSavingId(id);
    try {
      const response = await axiosPrivate.post(`/schedule-notifications/${id}/acknowledge`);
      updateNotification(response.data);
      toastSuccess("Potwierdzono odbiór powiadomienia.");
    } catch (error) {
      toastError(error?.response?.data?.message || "Nie udało się potwierdzić odbioru powiadomienia.");
    } finally {
      setSavingId(null);
    }
  };

  if (loading) return <div id="layoutSidenav_content"><div className="container-fluid px-4"><LoadingDataDiv/></div></div>;

  return (
    <div id="layoutSidenav_content">
      <div className="container-fluid px-4 py-3 schedule-notifications-page">
        <div className="schedule-page-heading">
          <div>
            <h4>Powiadomienia</h4>
            <div className="schedule-page-subtitle">Publikacje i zmiany w Twoim grafiku, decyzje dotyczące wniosków, zamiany oraz informacje od firmy.</div>
          </div>
          <div className="d-flex gap-2 align-items-center">
            <Form.Select className="schedule-notification-filter" value={filter} onChange={event => setFilter(event.target.value)}>
              <option value="ALL">Wszystkie</option>
              <option value="UNREAD">Nieprzeczytane</option>
              <option value="UNACKNOWLEDGED">Bez potwierdzenia odbioru</option>
            </Form.Select>
            <Button size="sm" variant="outline-secondary" onClick={load}>Odśwież</Button>
          </div>
        </div>

        {visible.length === 0 ? (
          <div className="schedule-empty">Brak powiadomień.</div>
        ) : (
          <div className="schedule-notification-list">
            {visible.map(item => (
              <div key={item.id} className={`schedule-notification-card ${item.readAt ? "" : "schedule-notification-unread"}`}>
                <div className="schedule-notification-card-heading">
                  <div>
                    <span className="schedule-notification-type">{typeLabels[item.type] || item.type}</span>
                    <h6>{item.title}</h6>
                  </div>
                  <span>{dateTime(item.createdAt)}</span>
                </div>
                <div className="schedule-notification-message">{item.message}</div>
                {item.scheduleName && <div className="schedule-notification-schedule">Grafik: {item.scheduleName}</div>}
                <div className="schedule-notification-delivery-summary">
                  <span>E-mail: <strong>{deliveryLabels[item.emailStatus] || item.emailStatus}</strong></span>
                  <span>SMS: <strong>{deliveryLabels[item.smsStatus] || item.smsStatus}</strong></span>
                  {item.smsDeliveredAt && <span>Dostarczono SMS: <strong>{dateTime(item.smsDeliveredAt)}</strong></span>}
                </div>
                <div className="schedule-notification-actions">
                  {!item.readAt && <Button size="sm" variant="outline-secondary" disabled={savingId === item.id} onClick={() => markRead(item.id)}>Oznacz jako przeczytane</Button>}
                  {!item.acknowledgedAt ? (
                    <Button size="sm" variant="primary" disabled={savingId === item.id} onClick={() => acknowledge(item.id)}>Potwierdź odbiór</Button>
                  ) : (
                    <span className="schedule-notification-ack">Odbiór potwierdzony {dateTime(item.acknowledgedAt)}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduleNotifications;
